import { createSlice } from '@reduxjs/toolkit';

import { requestData, changeOffset } from './Actions';

import type { InstanceType } from './Instances';

interface InstanceState {
  data: any[];
  fullData: Record<string, any>;
  page: number;
  total: number;
  offset: number;
  loading: boolean;
  error: string | null;
  pagination: boolean;
  paginationInProgress: boolean;
}

interface InstancesState {
  [key: string]: InstanceState;
}

const initialState: InstancesState = {};

// Initialize state for a specific instance
const initializeInstanceState = (instanceType: InstanceType): InstanceState => ({
  data: [],
  fullData: {},
  page: 1,
  total: 0,
  offset: 0,
  loading: false,
  error: null,
  pagination: false,
  paginationInProgress: false,
});

const instancesSlice = createSlice({
  name: 'instances',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Handle onLoad
      .addCase(requestData.pending, (state, action) => {
        const { instanceType } = action.meta.arg;
        if (!state[instanceType]) {
          state[instanceType] = initializeInstanceState(instanceType);
        }
        state[instanceType].loading = true;
        state[instanceType].error = null;
      })
      .addCase(requestData.fulfilled, (state, action) => {
        const { instanceType, data, dataKey = 'items' } = action.payload;
        const responseData = data as Record<string, any>;
        state[instanceType].data = responseData[dataKey] || [];
        state[instanceType].fullData = responseData;
        state[instanceType].total = responseData.total || 0;
        state[instanceType].loading = false;
      })
      .addCase(requestData.rejected, (state, action) => {
        const { instanceType } = action.meta.arg;
        state[instanceType].loading = false;
        state[instanceType].error = action.payload as string;
      })
      // Handle changeOffset
      // TODO: Handle other usecases
      //TODO: Complete ChangeOffset in actions and reducers
      .addCase(changeOffset.pending, (state, action) => {
        const { instanceType } = action.meta.arg;
        state[instanceType].paginationInProgress = true;
        state[instanceType].error = null;
      })
      .addCase(changeOffset.fulfilled, (state, action) => {
        const { instanceType, data, newOffset } = action.payload;
        const responseData = data as Record<string, any>;
        const dataKey = action.meta.arg.dataKey || 'items';
        state[instanceType].data = [...state[instanceType].data, ...(responseData[dataKey] || [])];
        state[instanceType].fullData = { ...state[instanceType].fullData, ...responseData };
        state[instanceType].offset = newOffset;
        state[instanceType].paginationInProgress = false;
      })
      .addCase(changeOffset.rejected, (state, action) => {
        const { instanceType } = action.meta.arg;
        state[instanceType].paginationInProgress = false;
        state[instanceType].error = action.payload as string;
      });
  },
});

export default instancesSlice.reducer; 